import { Link } from 'react-router-dom';
import { Check, X, ArrowRight, AlertTriangle, HelpCircle, ChevronDown, Trophy, Medal, Award, FileText, Globe, Video } from 'lucide-react';
import { useState, useEffect } from 'react';
import SEO from '../components/SEO';

const winnerTiers = [
  { name: 'DIGITAL', color: 'bg-blue-500', recommended: false },
  { name: 'CLASSIC', color: 'bg-gray-400', recommended: false },
  { name: 'DELUXE', color: 'bg-cyan-400', recommended: false },
  { name: 'SUPER DELUXE', color: 'bg-purple-500', recommended: true },
  { name: 'ROYAL', color: 'bg-amber-600', recommended: false },
];

// Pricing data with actual costs and shipping charges
const pricingTiers = {
  earlyBird: {
    dateRange: '1-Mar-2026 to 15-Mar-2026',
    startDate: new Date(2026, 2, 1), // Month is 0-indexed, so 2 = March
    endDate: new Date(2026, 2, 15, 23, 59, 59),
    prices: {
      digital: { display: 'USD 590', price: 590, actualCost: 0, shipping: 0 },
      classic: { display: 'USD 970', price: 970, actualCost: 0, shipping: 0 },
      deluxe: { display: 'USD 1,590', price: 1590, actualCost: 0, shipping: 0 },
      superDeluxe: { display: 'USD 1,980', price: 1980, actualCost: 0, shipping: 0 },
      royal: { display: 'USD 2,800', price: 2800, actualCost: 0, shipping: 0 },
    }
  },
  regular: {
    dateRange: '16-Mar-2026 to 25-Mar-2026',
    startDate: new Date(2026, 2, 16), // Month is 0-indexed
    endDate: new Date(2026, 2, 25, 23, 59, 59),
    prices: {
      digital: { display: 'USD 690', price: 690, actualCost: 0, shipping: 0 },
      classic: { display: 'USD 1,170', price: 1170, actualCost: 0, shipping: 0 },
      deluxe: { display: 'USD 1,890', price: 1890, actualCost: 0, shipping: 0 },
      superDeluxe: { display: 'USD 2,380', price: 2380, actualCost: 0, shipping: 0 },
      royal: { display: 'USD 3,360', price: 3360, actualCost: 0, shipping: 0 },
    }
  },
  lastMinute: {
    dateRange: '25-Mar-2026 to 15-Apr-2026',
    startDate: new Date(2026, 2, 25), // Month is 0-indexed
    endDate: new Date(2026, 3, 15, 23, 59, 59), // April = 3
    prices: {
      digital: { display: 'USD 830', price: 830, actualCost: 0, shipping: 0 },
      classic: { display: 'USD 1,400', price: 1400, actualCost: 0, shipping: 0 },
      deluxe: { display: 'USD 2,270', price: 2270, actualCost: 0, shipping: 0 },
      superDeluxe: { display: 'USD 2,860', price: 2860, actualCost: 0, shipping: 0 },
      royal: { display: 'USD 4,030', price: 4030, actualCost: 0, shipping: 0 },
    }
  }
};

const featureRows = [
  { feature: 'Complimentary Gala Entry Pass', digital: '1 Pass', classic: '2 Passes', deluxe: '3 Passes', superDeluxe: '4 Passes', royal: '5 Passes' },
  { feature: 'Featuring on Digital Magazine', digital: '1/2 Page', classic: '1 Page', deluxe: '1 Page', superDeluxe: '2 Pages', royal: '3 Pages' },
  { feature: 'Rights to Use Award Logo & Brand', digital: true, classic: true, deluxe: true, superDeluxe: true, royal: true },
  { feature: 'Winner Trophy - 12 inches', digital: false, classic: false, deluxe: false, superDeluxe: true, royal: true },
  { feature: 'Winner Plaque, A3 Size', digital: false, classic: false, deluxe: 'Black Acrylic', superDeluxe: 'Gold Color', royal: 'Gold Color' },
  { feature: 'Winner Medal', digital: false, classic: false, deluxe: true, superDeluxe: true, royal: true },
  { feature: 'Winner Certificate - Digital', digital: true, classic: true, deluxe: true, superDeluxe: true, royal: true },
  { feature: 'Winner Badge - Digital', digital: true, classic: true, deluxe: true, superDeluxe: true, royal: true },
  { feature: 'Winner Email Signature - Digital', digital: true, classic: true, deluxe: true, superDeluxe: true, royal: true },
  { feature: 'Highlighted on Awards Home Page', digital: false, classic: false, deluxe: true, superDeluxe: true, royal: true },
  { feature: "Listing on Winner's Section", digital: true, classic: true, deluxe: true, superDeluxe: true, royal: true },
  { feature: "Featured Winner's Section", digital: false, classic: false, deluxe: true, superDeluxe: true, royal: true },
  { feature: 'Link to Your Website', digital: true, classic: true, deluxe: true, superDeluxe: true, royal: true },
  { feature: 'Link to Social Media Platforms', digital: true, classic: true, deluxe: true, superDeluxe: true, royal: true },
  { feature: "Winner's Speech Video", digital: false, classic: false, deluxe: false, superDeluxe: true, royal: true },
  { feature: 'Facebook - Social Media Post', digital: false, classic: false, deluxe: false, superDeluxe: true, royal: true },
  { feature: 'Instagram - Social Media Post', digital: false, classic: false, deluxe: false, superDeluxe: true, royal: true },
  { feature: 'LinkedIn - Social Media Post', digital: false, classic: false, deluxe: false, superDeluxe: true, royal: true },
];

const packageBenefits = [
  { icon: Award, title: 'Rights to Use Award Logo', description: 'Winner package includes rights to use Award logo, Award name, Winner title and Announcement details.' },
  { icon: FileText, title: 'Complimentary Gala Entry', description: 'Winners get an opportunity to attend the gala ceremony with entry passes on first come first serve basis.' },
  { icon: FileText, title: 'Digital Materials', description: 'Customized winner badges, certificates, and email signatures for professional use.' },
  { icon: Trophy, title: 'Physical Winner Items', description: 'Trophies, Plaques, Medals & Stickers based on your selected package tier.' },
  { icon: Globe, title: 'Online Exposure', description: 'Featured listings on the awarding organization\'s website and winner section.' },
  { icon: Video, title: 'Promotional Opportunities', description: 'Speaking engagements, participation in industry events, or inclusion in mentorship programs.' },
];

const importantNotes = [
  'ONLY the winners are eligible to order the Winner Package.',
  'If you participated in the award program and NOT a winner then you CAN NOT order a winner package.',
  'Every nomination MUST go through First level Jury review, Voting and Final Jury Review to become a Winner.',
  'We do not guarantee winning an award if you nominate.',
  'The winner selection is 100% based on our Award Selection procedure / Criteria.',
  'Do NOT approach our team with any monetary benefit to influence the winning chances.',
  'Please read winner package terms and conditions before ordering winner package.',
];

const faqs = [
  {
    question: 'What is a winner package?',
    answer: 'A winner package includes promotional materials, trophies, certificates, digital assets, and marketing opportunities available exclusively to award winners to maximize the value of their achievement.',
  },
  {
    question: 'Can I order a winner package before winning?',
    answer: 'No, winner packages are exclusively available to those who have won an award. You must first complete the nomination process and be selected as a winner.',
  },
  {
    question: 'What is included in the digital materials?',
    answer: 'Digital materials include customized winner badges, certificates, email signatures, and social media graphics that you can use for promotional purposes.',
  },
  {
    question: 'How do I receive physical items like trophies?',
    answer: 'Physical items are shipped to your registered address after your winner package order is confirmed. Shipping times may vary based on your location.',
  },
  {
    question: 'Can I upgrade my winner package later?',
    answer: 'Yes, you can upgrade to a higher tier package by paying the difference. Contact our support team to process an upgrade.',
  },
  {
    question: 'What are the gala entry passes?',
    answer: 'Gala entry passes allow you to attend the prestigious award ceremony. The number of complimentary passes depends on your selected winner package tier.',
  },
];

// Helper function to check if current date is within a pricing tier's date range
const isDateInRange = (startDate, endDate) => {
  const today = new Date();
  return today >= startDate && today <= endDate;
};

// Get current active pricing tier
const getActivePricingTier = () => {
  if (isDateInRange(pricingTiers.earlyBird.startDate, pricingTiers.earlyBird.endDate)) {
    return 'earlyBird';
  } else if (isDateInRange(pricingTiers.regular.startDate, pricingTiers.regular.endDate)) {
    return 'regular';
  } else if (isDateInRange(pricingTiers.lastMinute.startDate, pricingTiers.lastMinute.endDate)) {
    return 'lastMinute';
  }
  return null;
};

// Order button component - always visible, submits to payment form
const OrderButton = ({ packageName, price, actualCost, shippingCharges }) => {
  const handleOrder = () => {
    // Set form values
    document.getElementById('package_name').value = packageName;
    document.getElementById('package_cost').value = price;
    document.getElementById('package_actual_cost').value = actualCost;
    document.getElementById('shipping_charges').value = shippingCharges;
    document.getElementById('currency_code').value = 'USD';
    
    // Submit form
    document.getElementById('form_order_winner_package').submit();
  };

  return (
    <button
      onClick={handleOrder}
      className="mt-2 bg-[#D4AF37] text-black text-xs font-bold px-4 py-2 uppercase tracking-wider hover:bg-[#C5A028] transition-colors"
    >
      Order Now
    </button>
  );
};

// Date-conditional Order button - only shows during active period
const ConditionalOrderButton = ({ packageName, price, actualCost, shippingCharges, isActive }) => {
  if (!isActive) {
    return null;
  }

  return (
    <OrderButton 
      packageName={packageName} 
      price={price} 
      actualCost={actualCost} 
      shippingCharges={shippingCharges} 
    />
  );
};

export const WinnerPackagePage = () => {
  const [openFaq, setOpenFaq] = useState(null);
  const [activeTier, setActiveTier] = useState(null);

  useEffect(() => {
    setActiveTier(getActivePricingTier());
  }, []);

  const renderCell = (value) => {
    if (value === true) return <Check className="w-5 h-5 text-green-500 mx-auto" />;
    if (value === false) return <X className="w-5 h-5 text-red-500 mx-auto" />;
    return <span className="text-white text-sm">{value}</span>;
  };

  const packageNames = {
    digital: 'WP - Digital',
    classic: 'WP - Classic',
    deluxe: 'WP - Deluxe',
    superDeluxe: 'WP - Super Deluxe',
    royal: 'WP - Royal',
  };

  return (
    <main data-testid="winner-package-page" className="pt-24 min-h-screen bg-[#0a0a0a]">
      <SEO 
        title="Winner Package - Award Trophy & Recognition Packages"
        description="Choose your Golden Tree Events winner package. Options from Digital to Royal tier include trophies, certificates, media coverage, PR features, and worldwide recognition. Early bird pricing available."
      />
      {/* Hidden Form for Order Submission */}
      <form id="form_order_winner_package" action="https://gtedubai.com/app/pay" method="POST">
        <input type="hidden" name="country_id" id="country_id" />
        <input type="hidden" name="package_name" id="package_name" />
        <input type="hidden" name="package_cost" id="package_cost" />
        <input type="hidden" name="package_actual_cost" id="package_actual_cost" />
        <input type="hidden" name="shipping_charges" id="shipping_charges" />
        <input type="hidden" name="award_id" value="1" />
        <input type="hidden" name="invoice_type_id" value="1" />
        <input type="hidden" name="currency_code" id="currency_code" value="USD" />
      </form>

      {/* Hero Section */}
      <section className="py-16 md:py-20 border-b border-white/10 relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-r from-[#D4AF37]/10 via-transparent to-[#D4AF37]/10" />
        <div className="w-full px-4 sm:px-8 lg:px-16 relative z-10">
          <div className="text-center">
            <p className="font-google-sans text-[#D4AF37] text-xs tracking-[0.3em] uppercase mb-4">Fee / Cost Involved</p>
            <h1 className="font-montserrat text-4xl sm:text-5xl lg:text-6xl text-white mb-6">
              Winner Package <span className="gradient-gold">Pricing</span>
            </h1>
            <p className="font-google-sans text-[#A1A1AA] text-base max-w-3xl mx-auto leading-relaxed">
              Winner packages offer promotional opportunities and benefits to maximize the value of your achievement. 
              The pricing varies depending on the winner package materials included.
            </p>
          </div>
        </div>
      </section>

      {/* Package Benefits */}
      <section className="py-16">
        <div className="w-full px-4 sm:px-8 lg:px-16">
          <div className="text-center mb-12">
            <h2 className="font-montserrat text-3xl text-white">
              What's <span className="gradient-gold">Included</span>
            </h2>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {packageBenefits.map((benefit, index) => (
              <div key={index} className="p-6 bg-white/[0.02] border border-white/10">
                <benefit.icon className="w-8 h-8 text-[#D4AF37] mb-4" />
                <h3 className="font-montserrat text-white text-lg mb-2">{benefit.title}</h3>
                <p className="font-google-sans text-[#A1A1AA] text-sm">{benefit.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Award Categories Section - 2 Columns */}
      <section className="py-16 bg-[#121212]">
        <div className="w-full px-4 sm:px-8 lg:px-16">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {/* Business Awards Column */}
            <div data-testid="business-awards-card" className="bg-white/[0.02] border border-white/10 rounded-lg overflow-hidden">
              <div className="p-8">
                <h3 className="font-montserrat text-2xl text-white text-center mb-6">
                  For <span className="gradient-gold">Business Awards</span>
                </h3>
                <div className="flex justify-center mb-8">
                  <img 
                    src="https://customer-assets.emergentagent.com/job_25158215-48c8-4c0f-9133-9d7af68b124e/artifacts/jqt5rtvz_Hospitality%20%26%20Business%20Awards%20-%20Trophy.png" 
                    alt="Business Awards Trophy" 
                    className="h-[30rem] w-auto object-contain"
                  />
                </div>
                <div className="border-t border-white/10 pt-6">
                  <h4 className="font-montserrat text-[#D4AF37] text-lg mb-4">Applicable for:</h4>
                  <ul className="grid grid-cols-2 gap-3">
                    {['International Travel Awards', 'International Restaurant Awards', 'International Spa & Beauty Awards', 'World Star Hotel Awards', 'Arabian Best of Best Awards', 'Europe Best of Best Awards', 'UK Best of Best Awards', 'Asia Best of Best Awards', 'America Best of Best Awards', 'Africa Best of Best Awards', 'Oceania Best of Best Awards', 'World Beauty Awards', 'World Real Estate Excellence Awards'].map((award, i) => (
                      <li key={i} className="flex items-center gap-3 text-[#A1A1AA]">
                        <Check className="w-5 h-5 text-[#D4AF37] flex-shrink-0" />
                        <span>{award}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </div>

            {/* Individual Awards Column */}
            <div data-testid="individual-awards-card" className="bg-white/[0.02] border border-white/10 rounded-lg overflow-hidden">
              <div className="p-8">
                <h3 className="font-montserrat text-2xl text-white text-center mb-6">
                  <span className="gradient-gold">Individual</span> Awards
                </h3>
                <div className="flex justify-center mb-8">
                  <img 
                    src="https://customer-assets.emergentagent.com/job_25158215-48c8-4c0f-9133-9d7af68b124e/artifacts/232nc9pz_Individual%20Awards%20-%20Trophy.png" 
                    alt="Individual Awards Trophy" 
                    className="h-[30rem] w-auto object-contain"
                  />
                </div>
                <div className="border-t border-white/10 pt-6">
                  <h4 className="font-montserrat text-[#D4AF37] text-lg mb-4">Applicable for:</h4>
                  <ul className="grid grid-cols-2 gap-3">
                    {['World GM Awards', 'World CEO Awards', 'Golden Entrepreneur Awards', 'Golden Women Awards'].map((award, i) => (
                      <li key={i} className="flex items-center gap-3 text-[#A1A1AA]">
                        <Check className="w-5 h-5 text-[#D4AF37] flex-shrink-0" />
                        <span>{award}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </div>
          </div>

          {/* Plaque & Medal Row */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mt-8">
            {/* Winner Plaque Column */}
            <div data-testid="winner-plaque" className="bg-white/[0.02] border border-white/10 rounded-lg overflow-hidden">
              <div className="p-8">
                <h3 className="font-montserrat text-2xl text-white text-center mb-6">
                  Winner <span className="gradient-gold">Plaque</span>
                </h3>
                <div className="flex justify-center mb-8">
                  <img 
                    src="https://customer-assets.emergentagent.com/job_25158215-48c8-4c0f-9133-9d7af68b124e/artifacts/s78fszf9_Plaque.png" 
                    alt="Winner Plaque" 
                    className="h-[30rem] w-auto object-contain"
                  />
                </div>
                <div className="border-t border-white/10 pt-6 text-center">
                  <p className="font-google-sans text-[#A1A1AA] text-sm">A3 Size - Gold Color Frame</p>
                </div>
              </div>
            </div>

            {/* Winner Medal Column */}
            <div data-testid="winner-medal" className="bg-white/[0.02] border border-white/10 rounded-lg overflow-hidden">
              <div className="p-8">
                <h3 className="font-montserrat text-2xl text-white text-center mb-6">
                  Winner <span className="gradient-gold">Medal</span>
                </h3>
                <div className="flex justify-center mb-8">
                  <img 
                    src="https://customer-assets.emergentagent.com/job_25158215-48c8-4c0f-9133-9d7af68b124e/artifacts/c0zhvr56_Medal.png" 
                    alt="Winner Medal" 
                    className="h-[30rem] w-auto object-contain"
                  />
                </div>
                <div className="border-t border-white/10 pt-6 text-center">
                  <p className="font-google-sans text-[#A1A1AA] text-sm">Premium Gold Medal with Ribbon</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Important Notes */}
      <section className="py-12 bg-red-500/10 border-y border-red-500/30">
        <div className="w-full px-4 sm:px-8 lg:px-16">
          <div className="flex items-center gap-3 mb-6">
            <AlertTriangle className="w-6 h-6 text-red-500" />
            <h3 className="font-montserrat text-white text-xl">Important Notes</h3>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {importantNotes.map((note, index) => (
              <div key={index} className="flex items-start gap-3">
                <span className="w-6 h-6 bg-red-500/20 text-red-500 flex items-center justify-center text-xs flex-shrink-0">
                  {index + 1}
                </span>
                <p className="text-[#A1A1AA] text-sm">{note}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Pricing Table */}
      <section className="py-16 bg-[#121212]">
        <div className="w-full px-4 sm:px-8 lg:px-16">
          <div className="text-center mb-12">
            <h2 className="font-montserrat text-3xl sm:text-4xl text-white mb-4">
              Winner Package <span className="gradient-gold">Pricing Table</span>
            </h2>
          </div>

          {/* Table */}
          <div className="relative">
            <table className="w-full border-collapse">
              <thead>
                <tr className="sticky top-[72px] z-30">
                  <th className="text-left p-4 bg-[#1a1a1a] text-white font-google-sans text-sm uppercase tracking-wider border border-white/10">
                    Features
                  </th>
                  {winnerTiers.map((tier) => (
                    <th
                      key={tier.name}
                      className={`p-4 text-center border border-white/10 ${
                        tier.recommended ? 'bg-[#3d3419]' : 'bg-[#1a1a1a]'
                      }`}
                    >
                      {tier.recommended && (
                        <span className="bg-red-500 text-white text-xs px-3 py-1 uppercase tracking-wider block mb-2">
                          Recommended
                        </span>
                      )}
                      <span className={`inline-block px-2 py-1 text-black text-xs font-bold uppercase ${tier.color}`}>
                        {tier.name}
                      </span>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {/* Early Bird Entry Price Row */}
                <tr className="bg-[#0f0f0f]">
                  <td className="p-4 text-[#A1A1AA] text-sm border border-white/10 font-medium">
                    <div>Early Bird Entry Price</div>
                    <div className="text-[#D4AF37] text-xs mt-1 animate-pulse font-semibold">
                      ({pricingTiers.earlyBird.dateRange})
                    </div>
                  </td>
                  {['digital', 'classic', 'deluxe', 'superDeluxe', 'royal'].map((tier) => (
                    <td key={tier} className={`p-4 text-center border border-white/10 ${tier === 'superDeluxe' ? 'bg-[#D4AF37]/5' : ''}`}>
                      <div className="text-white/50 text-xs line-through mb-1">
                        {pricingTiers.regular.prices[tier].display}
                      </div>
                      <div className="text-white font-bold text-lg">
                        {pricingTiers.earlyBird.prices[tier].display}
                      </div>
                      <ConditionalOrderButton
                        packageName={packageNames[tier]}
                        price={pricingTiers.earlyBird.prices[tier].price}
                        actualCost={pricingTiers.earlyBird.prices[tier].actualCost}
                        shippingCharges={pricingTiers.earlyBird.prices[tier].shipping}
                        isActive={activeTier === 'earlyBird'}
                      />
                    </td>
                  ))}
                </tr>

                {/* Regular Entry Price Row */}
                <tr className="bg-[#151515]">
                  <td className="p-4 text-[#A1A1AA] text-sm border border-white/10 font-medium">
                    <div>Regular Entry Price</div>
                    <div className="text-[#D4AF37] text-xs mt-1 animate-pulse font-semibold">
                      ({pricingTiers.regular.dateRange})
                    </div>
                  </td>
                  {['digital', 'classic', 'deluxe', 'superDeluxe', 'royal'].map((tier) => (
                    <td key={tier} className={`p-4 text-center border border-white/10 ${tier === 'superDeluxe' ? 'bg-[#D4AF37]/5' : ''}`}>
                      <div className="text-white font-bold">
                        {pricingTiers.regular.prices[tier].display}
                      </div>
                      <ConditionalOrderButton
                        packageName={packageNames[tier]}
                        price={pricingTiers.regular.prices[tier].price}
                        actualCost={pricingTiers.regular.prices[tier].actualCost}
                        shippingCharges={pricingTiers.regular.prices[tier].shipping}
                        isActive={activeTier === 'regular'}
                      />
                    </td>
                  ))}
                </tr>

                {/* Last Minute Entry Price Row */}
                <tr className="bg-[#0f0f0f]">
                  <td className="p-4 text-[#A1A1AA] text-sm border border-white/10 font-medium">
                    <div>Last Minute Entry Price</div>
                    <div className="text-[#D4AF37] text-xs mt-1 animate-pulse font-semibold">
                      ({pricingTiers.lastMinute.dateRange})
                    </div>
                  </td>
                  {['digital', 'classic', 'deluxe', 'superDeluxe', 'royal'].map((tier) => (
                    <td key={tier} className={`p-4 text-center border border-white/10 ${tier === 'superDeluxe' ? 'bg-[#D4AF37]/5' : ''}`}>
                      <div className="text-white font-bold">
                        {pricingTiers.lastMinute.prices[tier].display}
                      </div>
                      <ConditionalOrderButton
                        packageName={packageNames[tier]}
                        price={pricingTiers.lastMinute.prices[tier].price}
                        actualCost={pricingTiers.lastMinute.prices[tier].actualCost}
                        shippingCharges={pricingTiers.lastMinute.prices[tier].shipping}
                        isActive={activeTier === 'lastMinute'}
                      />
                    </td>
                  ))}
                </tr>

                {/* Feature rows */}
                {featureRows.map((row, index) => (
                  <tr key={index} className={index % 2 === 0 ? 'bg-[#151515]' : 'bg-[#0f0f0f]'}>
                    <td className="p-4 text-[#A1A1AA] text-sm border border-white/10 font-medium">
                      {row.feature}
                    </td>
                    <td className="p-4 text-center border border-white/10">{renderCell(row.digital)}</td>
                    <td className="p-4 text-center border border-white/10">{renderCell(row.classic)}</td>
                    <td className="p-4 text-center border border-white/10">{renderCell(row.deluxe)}</td>
                    <td className="p-4 text-center border border-white/10 bg-[#D4AF37]/5">{renderCell(row.superDeluxe)}</td>
                    <td className="p-4 text-center border border-white/10">{renderCell(row.royal)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* CTA */}
          <div className="text-center mt-12">
            <p className="text-[#A1A1AA] text-sm mb-6">
              Winner packages can only be purchased after winning an award.
            </p>
            <a
              href="https://goldentreeawards.com/award-nomination"
              target="_blank"
              rel="noopener noreferrer"
              data-testid="nominate-now-btn"
              className="inline-flex items-center gap-3 bg-[#D4AF37] text-black font-medium px-10 py-4 uppercase tracking-widest text-sm hover:bg-[#C5A028] transition-all duration-300"
            >
              Start with Nomination
              <ArrowRight className="w-4 h-4" />
            </a>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-16 bg-[#0a0a0a]">
        <div className="w-full px-4 sm:px-8 lg:px-16">
          <div className="text-center mb-12">
            <p className="font-google-sans text-[#D4AF37] text-xs tracking-[0.3em] uppercase mb-4">FAQ</p>
            <h2 className="font-montserrat text-3xl sm:text-4xl text-white">
              Frequently Asked <span className="gradient-gold">Questions</span>
            </h2>
          </div>

          <div className="max-w-4xl mx-auto space-y-4">
            {faqs.map((faq, index) => (
              <div
                key={index}
                className="border border-white/10 bg-white/[0.02]"
              >
                <button
                  onClick={() => setOpenFaq(openFaq === index ? null : index)}
                  className="w-full flex items-center justify-between p-6 text-left"
                  data-testid={`faq-winner-${index}`}
                >
                  <div className="flex items-center gap-3">
                    <HelpCircle className="w-5 h-5 text-[#D4AF37] flex-shrink-0" />
                    <span className="font-google-sans text-white font-medium">{faq.question}</span>
                  </div>
                  <ChevronDown className={`w-5 h-5 text-[#D4AF37] transition-transform ${openFaq === index ? 'rotate-180' : ''}`} />
                </button>
                <div className={`overflow-hidden transition-all duration-300 ${openFaq === index ? 'max-h-40 pb-6' : 'max-h-0'}`}>
                  <p className="px-6 pl-14 text-[#A1A1AA] text-sm leading-relaxed">{faq.answer}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* View Registration Fee Link */}
      <section className="py-12 border-t border-white/10">
        <div className="w-full px-4 sm:px-8 lg:px-16 text-center">
          <Link
            to="/registration-fee"
            data-testid="view-registration-fee"
            className="inline-flex items-center gap-2 text-[#D4AF37] font-medium uppercase tracking-wider hover:gap-3 transition-all duration-300"
          >
            View Registration Fee
            <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
      </section>
    </main>
  );
};

export default WinnerPackagePage;
